import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { usePatchingStore, CableData } from '../../stores/usePatchingStore';
import { PortGenerator } from './PortGenerator';

interface PortData {
  id: string;
  name: string;
  port_type: string;
  connector_type?: string;
}

export function PatchPanelMatrix({ equipmentId, isLeftPanel }: { equipmentId: string, isLeftPanel: boolean }) {
  const [ports, setPorts] = useState<PortData[]>([]);
  const [portStatuses, setPortStatuses] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const store = usePatchingStore();
  const selectedPorts = isLeftPanel ? store.selectedLeftPorts : store.selectedRightPorts;

  const fetchPorts = async () => {
    setLoading(true);
    try {
      const [portsRes, cablesRes] = await Promise.all([
        api.get(`/api/equipment/${equipmentId}/ports`),
        api.get(`/api/equipment/${equipmentId}/cables`)
      ]);
      setPorts(portsRes.data);

      // [D2] Сохраняем данные о кабелях в стор (для Commit All)
      const cables: CableData[] = cablesRes.data;
      if (isLeftPanel) store.setLeftCables(cables);
      else store.setRightCables(cables);

      const statuses = new Map<string, string>();
      cables.forEach((c: CableData) => {
        statuses.set(c.port_a_id, c.status || 'Live');
        statuses.set(c.port_b_id, c.status || 'Live');
      });
      setPortStatuses(statuses);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPorts();
  }, [equipmentId, store.patchingTrigger]);

  if (loading) return <div className="p-4 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>Loading ports...</div>;

  if (ports.length === 0) {
    return <PortGenerator equipmentId={equipmentId} onPortsGenerated={fetchPorts} />;
  }

  const handlePortClick = (e: React.MouseEvent, portId: string) => {
    if (e.shiftKey) {
      // Bulk select logic
      const lastSelected = selectedPorts[selectedPorts.length - 1];
      if (lastSelected) {
        const lastIdx = ports.findIndex(p => p.id === lastSelected);
        const currIdx = ports.findIndex(p => p.id === portId);
        if (lastIdx !== -1 && currIdx !== -1) {
          const start = Math.min(lastIdx, currIdx);
          const end = Math.max(lastIdx, currIdx);
          const newSelection = ports.slice(start, end + 1).map(p => p.id);
          if (isLeftPanel) store.setLeftPorts(newSelection);
          else store.setRightPorts(newSelection);
          return;
        }
      }
    }
    
    if (isLeftPanel) store.toggleLeftPort(portId);
    else store.toggleRightPort(portId);
  };

  return (
    <div
      className="p-4 rounded shadow-lg max-h-[60vh] overflow-auto border"
      style={{
        backgroundColor: 'var(--bg-panel)',
        borderColor: 'var(--border-color)',
        color: 'var(--text-primary)',
      }}
    >
      <div className="mb-4 flex justify-between items-center border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
        <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>Ports ({ports.length})</h3>
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Selected: <span className="text-blue-500 font-bold">{selectedPorts.length}</span>
        </div>
      </div>
      
      {/* Dynamic grid layout to match standard 24/48 switches */}
      <div className="grid grid-cols-12 gap-1.5">
        {ports.map((port) => {
          const isSelected = selectedPorts.includes(port.id);
          const pStatus = portStatuses.get(port.id);
          // Show just numbers if possible, otherwise short text
          const num = port.name.replace(/[^0-9]/g, '');
          const label = num ? num : port.name.substring(0, 3);
          
          let bgClass = 'border hover:border-[var(--border-focus)]';
          let portStyle: React.CSSProperties = {
            backgroundColor: 'var(--bg-app)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
          };

          if (isSelected) {
            bgClass = 'shadow-[0_0_8px_rgba(59,130,246,0.5)] border-blue-400';
            portStyle = { backgroundColor: 'var(--accent)', borderColor: '#60a5fa', color: '#ffffff' };
          } else if (pStatus === 'Live') {
            bgClass = 'hover:opacity-90 border-green-500';
            portStyle = { backgroundColor: '#15803d', borderColor: '#22c55e', color: '#ffffff' };
          } else if (pStatus === 'Planned') {
            bgClass = 'hover:opacity-90 border-orange-500';
            portStyle = { backgroundColor: '#c2410c', borderColor: '#f97316', color: '#ffffff' };
          } else if (pStatus === 'Faulted') {
            bgClass = 'hover:opacity-90 border-red-500';
            portStyle = { backgroundColor: '#b91c1c', borderColor: '#ef4444', color: '#ffffff' };
          }
          
          return (
            <button
              key={port.id}
              onClick={(e) => handlePortClick(e, port.id)}
              className={`
                h-10 w-10 flex flex-col items-center justify-center rounded border transition-all relative
                ${bgClass}
              `}
              style={portStyle}
              title={`${port.name} (${port.port_type})`}
            >
              <div className="w-5 h-2.5 bg-black/40 border-b-2 border-black/20 rounded-sm mb-1" />
              <span className="text-[10px] font-mono font-bold">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
